import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import API from '../../../services/API'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Alert, Divider } from '@mui/material'
import ShiftClockSettingsForm from './Employees/ShiftClock'

const ShiftConfig = () => {
  const settings = useContext(TenantSettingsContext).orgConfig?.orgSettings
  const roles = settings?.roles ?? []
  const [rates, setRates] = useState(settings)
  const [defaultTravelRate, setDefaultTravelRate] = useState('')
  const [shiftBookingPermissions, setShiftBookingPermissions] = useState({})
  const [
    sendShiftApplicationRefusedEmails,
    setSendShiftApplicationRefusedEmails,
  ] = useState(true)
  const [maxApplicationsPerShift, setMaxApplicationsPerShift] = useState(0)
  const [shiftAvailabilityNotifications, setShiftAvailabilityNotifications] =
    useState({ automatic: true, manual: true })
  const [shiftClock, setShiftClock] = useState({})
  const [onCallDashboardVisibility, setOnCallDashboardVisibility] =
    useState('everyone')
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig } = useContext(TenantSettingsContext)

  const updateShiftBookingPermission = (permission) => {
    setShiftBookingPermissions((prev) => ({
      ...prev,
      [permission]: !prev?.[permission],
    }))
  }

  useEffect(() => {
    setRates(settings?.defaultPayRates ?? {})
    setDefaultTravelRate(settings?.defaultTravelRate ?? '')
    setShiftBookingPermissions(settings?.shiftBookingPermissions ?? {})
    setSendShiftApplicationRefusedEmails(
      settings?.sendShiftApplicationRefusedEmails ?? false,
    )
    setMaxApplicationsPerShift(settings?.maxApplicationsPerShift ?? 0)
    // Missing = enabled: older settings documents predate these switches.
    setShiftAvailabilityNotifications({
      automatic: settings?.shiftAvailabilityNotifications?.automatic !== false,
      manual: settings?.shiftAvailabilityNotifications?.manual !== false,
    })
    setShiftClock(settings?.shiftClock ?? {})
    setOnCallDashboardVisibility(
      settings?.onCallSettings?.dashboardVisibility ?? 'everyone',
    )
  }, [settings])

  const handleSaveChanges = (e) => {
    e.preventDefault()
    API.put('/site-config', {
      defaultPayRates: rates,
      defaultTravelRate:
        defaultTravelRate === '' ? null : Number(defaultTravelRate),
      shiftBookingPermissions,
      sendShiftApplicationRefusedEmails,
      maxApplicationsPerShift,
      shiftAvailabilityNotifications,
      shiftClock,
      onCallSettings: { dashboardVisibility: onCallDashboardVisibility },
    }).then((res) => {
      if (res.status === 200) {
        setToast(true)
        setOrgConfig((prev) => ({
          ...prev,
          orgSettings: {
            ...prev.orgSettings,
            ...res.data,
          },
        }))
      }
    })
  }

  return (
    <Stack gap={3} component="form" onSubmit={handleSaveChanges}>
      <Stack padding={1} gap={1}>
        <Typography variant="title2">Shift Configuration</Typography>

        <Typography variant="title3" fontSize="1rem">
          Default Hourly Pay Rates
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {roles.map((role) => {
            return (
              <TextField
                key={role}
                value={rates?.[role] ?? 0}
                required
                label={role}
                type="number"
                onChange={(e) =>
                  setRates((prev) => ({
                    ...prev,
                    [role]: e.target.value,
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {settings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">/hr</InputAdornment>
                  ),
                }}
              />
            )
          })}
          {!roles?.length && (
            <Alert
              severity="info"
              action={
                <Button onClick={() => navigate('/site-configuration/staff')}>
                  Edit Roles
                </Button>
              }
            >
              There are currently no enabled roles.
            </Alert>
          )}
        </Stack>

        <Typography variant="title3" fontSize="1rem" mt={1}>
          Paid Travel Time
        </Typography>
        <Stack gap={0.5} alignItems="flex-start">
          <TextField
            value={defaultTravelRate}
            label="Default travel rate"
            type="number"
            inputProps={{ min: 0, step: 0.5 }}
            onChange={(e) => setDefaultTravelRate(e.target.value)}
            onWheel={(e) => e.target.blur()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {settings?.currency ?? '£'}
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">/hr</InputAdornment>,
            }}
          />
          <Typography variant="caption">
            Pre-fills the hourly rate when paid travel time is added to a
            shift (travel minutes either side of the shift, paid on top of
            the shift rate). Each shift can override it.
          </Typography>
        </Stack>
      </Stack>

      <Typography variant="title3">Shift Applications</Typography>
      <Stack gap={1} divider={<Divider />}>
        <Stack>
          <FormControlLabel
            label="Prevent applications with expired mandatory training"
            control={
              <Checkbox
                checked={
                  shiftBookingPermissions?.preventExpiredTraining ?? false
                }
                onChange={() =>
                  updateShiftBookingPermission('preventExpiredTraining')
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff cannot request shifts while mandatory training
            required for their clinical role is missing or expired.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Prevent applications with Invalid DBS Checks"
            control={
              <Checkbox
                checked={shiftBookingPermissions?.preventInvalidDBS ?? false}
                onChange={() =>
                  updateShiftBookingPermission('preventInvalidDBS')
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff cannot request shifts while they do not have a
            valid DBS check.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Prevent applications when occupational health screening is outstanding"
            control={
              <Checkbox
                checked={
                  shiftBookingPermissions?.preventOccupationalHealthDue ??
                  false
                }
                onChange={() =>
                  updateShiftBookingPermission('preventOccupationalHealthDue')
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff cannot request shifts while they have never
            completed an occupational health screening or a new one has been
            requested from them (see Configuration &gt; Employees &gt;
            Occupational Health).
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Prevent applications when already assigned to a shift"
            control={
              <Checkbox
                checked={
                  shiftBookingPermissions?.preventIfHasShiftAssigned ?? false
                }
                onChange={() =>
                  updateShiftBookingPermission('preventIfHasShiftAssigned')
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff cannot request a shift that conflicts with a
            shift already assigned to them.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Prevent applications when another application is in progress"
            control={
              <Checkbox
                checked={
                  shiftBookingPermissions?.preventIfHasShiftApplicationInProgress ??
                  false
                }
                onChange={() =>
                  updateShiftBookingPermission(
                    'preventIfHasShiftApplicationInProgress',
                  )
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff cannot request a shift that conflicts with
            another pending shift application.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Send Shift Application Rejection Notifications"
            control={
              <Checkbox
                checked={sendShiftApplicationRefusedEmails}
                onChange={() =>
                  setSendShiftApplicationRefusedEmails((prev) => !prev)
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, employees will receive a notification when their shift
            application has been rejected. Employees will always receive
            notifications when their shift application has been accepted.
          </Typography>
        </Stack>
        <Stack>
          <Stack direction="row" flexWrap="wrap" alignItems="flex-end" gap={1}>
            <Typography>Restrict applications to</Typography>
            <TextField
              type="number"
              inputProps={{ min: 0 }}
              value={maxApplicationsPerShift}
              autoComplete="off"
              onChange={(e) => setMaxApplicationsPerShift(e.target.value)}
              size="small"
              sx={{
                width: 70,
                '& .MuiInputBase-input': {
                  padding: '6px 8px',
                  textAlign: 'center',
                },
              }}
            />
            <Typography>applications per shift</Typography>
          </Stack>
          <Typography variant="caption">
            Set to Zero to disable this restriction
          </Typography>
        </Stack>
      </Stack>

      <Typography variant="title3">Available Shift Notifications</Typography>
      <Stack gap={1} divider={<Divider />}>
        <Stack>
          <FormControlLabel
            label="Send automatic notifications about available shifts"
            control={
              <Checkbox
                checked={shiftAvailabilityNotifications.automatic}
                onChange={() =>
                  setShiftAvailabilityNotifications((prev) => ({
                    ...prev,
                    automatic: !prev.automatic,
                  }))
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, staff whose enabled roles match a vacant bookable
            shift receive a daily digest of newly added shifts (3pm) and a
            weekly digest of upcoming open shifts (Thursday afternoon), by
            email and/or app push according to their own notification
            preferences. Turn this off to stop both digests for the whole
            organisation.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            label="Allow managers to notify staff about available shifts on demand"
            control={
              <Checkbox
                checked={shiftAvailabilityNotifications.manual}
                onChange={() =>
                  setShiftAvailabilityNotifications((prev) => ({
                    ...prev,
                    manual: !prev.manual,
                  }))
                }
                color="contrast"
              />
            }
          />
          <Typography variant="caption">
            When enabled, the &quot;notify staff of available shifts&quot;
            button on an event&apos;s Shifts tab and on PTS Uncovered Shifts
            sends an immediate notification to eligible staff. Turn this off to
            remove the button.
          </Typography>
        </Stack>
      </Stack>

      <ShiftClockSettingsForm values={shiftClock} setValues={setShiftClock} />

      <Divider />

      <Stack padding={1} gap={1}>
        <Typography variant="title3">On-Call Rota</Typography>
        <FormControl fullWidth>
          <InputLabel>Home screen visibility</InputLabel>
          <Select
            value={onCallDashboardVisibility}
            label="Home screen visibility"
            onChange={(e) => setOnCallDashboardVisibility(e.target.value)}
          >
            <MenuItem value="everyone">Visible to everyone</MenuItem>
            <MenuItem value="managersSupervisors">
              Managers and Supervisors only
            </MenuItem>
            <MenuItem value="hidden">Hidden</MenuItem>
          </Select>
          <FormHelperText>
            Controls who sees the &quot;On Call Now&quot; card on the home
            screen. This never affects visibility in the Global Roster, and
            staff on the on-call rota can always see their own shifts.
          </FormHelperText>
        </FormControl>
      </Stack>

      <Button
        color="success"
        type="submit"
        variant="contained"
        size="small"
        fullWidth
      >
        Save Changes
      </Button>
    </Stack>
  )
}

export default ShiftConfig
